import {
  getDynamicMediaPath,
  getFileNameWithoutExtension,
  hideBreakLinesFromScreenReaders,
} from '@msc-b2c/shared-utils';

export function languageNavigator() {
  const ROOT_SEL = '.msc-langnav';
  const CORE_SEL = '.languagenavigation';

  const ITEM_SELECTORS = ['.cmp-language-navigator__item', '.cmp-languagenavigation__item'];
  const LINK_SELECTORS = [
    '.cmp-language-navigator__item-link',
    '.cmp-languagenavigation__item-link',
  ];
  const SEP_CLASSES = [
    'cmp-language-navigator__item--separator',
    'cmp-languagenavigation__item--separator',
  ];

  const FLAG_CLASS = 'cmp-language-navigator__flag';
  const FLAGS_BASE = '/content/dam/msc-cruises/b2c-assets/icons/flags';
  const LANG_COOKIE = 'b2cw-ch-user-preferred-language';

  function resolveFlagSrc(assetCodeOrPath: string): string {
    const code = getFileNameWithoutExtension(assetCodeOrPath) || assetCodeOrPath;
    return getDynamicMediaPath(code) || `${FLAGS_BASE}/${code}.svg`;
  }

  const initialized = new WeakSet<HTMLElement>();
  const coreInitialized = new WeakSet<HTMLElement>();

  function qsa<T extends Element>(root: ParentNode, selectors: string[]): T[] {
    return selectors.flatMap((sel) => Array.from(root.querySelectorAll<T>(sel)));
  }

  function setCookie(name: string, value: string, days = 180) {
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${encodeURIComponent(
      value
    )}; path=/; max-age=${maxAge}; SameSite=Lax`;
  }

  function getCurrentCountry(): string | null {
    const meta = document.querySelector('meta[name="countryCodeIso2"]');
    const v = meta?.getAttribute('content')?.trim().toLowerCase();
    return v && v.length === 2 ? v : null;
  }

  function parseHreflang(raw: string | null): { country?: string; language?: string } {
    if (!raw) return {};
    const norm = raw.replace(/\s+/g, '').replace(/_/g, '-').toLowerCase();
    const parts = norm.split('-').filter(Boolean);
    if (!parts.length) return {};
    const language = parts[0];
    let country: string | undefined;
    for (let i = parts.length - 1; i >= 0; i--) {
      if (parts[i].length === 2) {
        country = parts[i];
        break;
      }
    }
    if (!country && parts.length >= 2 && parts[1].length === 2) country = parts[1];
    return { country, language };
  }

  function isSeparator(li: HTMLElement): boolean {
    return (
      SEP_CLASSES.some((c) => li.classList.contains(c)) || li.getAttribute('role') === 'separator'
    );
  }

  // NEW: Language-agnostic identification of the search input
  function looksLikeCoreFormTextInput(el: Element): el is HTMLInputElement {
    return (
      el instanceof HTMLInputElement &&
      (el.classList.contains('cmp-form-text__text') ||
        el.getAttribute('data-cmp-hook-form-text') === 'input')
    );
  }

  function isGoodTextInput(el: Element): el is HTMLInputElement {
    return (
      el instanceof HTMLInputElement && !el.disabled && (el.type === 'search' || el.type === 'text')
    );
  }

  function isVisible(el: Element): boolean {
    // simple visibility check to avoid hidden duplicates
    const any = el as HTMLElement;
    return !!(any.offsetParent || any?.getClientRects().length);
  }

  function findNearestSearchInput(root: HTMLElement): HTMLInputElement | null {
    // Try to scope to the surrounding modal/container to avoid grabbing unrelated inputs
    const scope =
      root.closest('.language__modal, .cmp-experiencefragment, .cmp-container') || document;

    const candidates = Array.from(
      (scope as ParentNode).querySelectorAll<HTMLInputElement>(
        '.cmp-form-text__text,[data-cmp-hook-form-text="input"],input[type="search"],input[type="text"]'
      )
    ).filter((el) => looksLikeCoreFormTextInput(el) || isGoodTextInput(el));

    if (!candidates.length) return null;

    // Prefer a candidate that appears before the list and is visible
    const beforeAndVisible = candidates.filter(
      (inp) =>
        isVisible(inp) &&
        (inp.compareDocumentPosition(root) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
    );
    if (beforeAndVisible.length) return beforeAndVisible[beforeAndVisible.length - 1];

    // Otherwise any visible candidate in the same scope
    const firstVisible = candidates.find(isVisible);
    return firstVisible || candidates[0] || null;
  }

  function textOfItem(li: HTMLElement): string {
    const a = li.querySelector('a');
    const raw = (a?.textContent ?? li.textContent ?? '').trim();
    return raw.toLowerCase();
  }

  function addFlags(root: HTMLElement) {
    const links = qsa<HTMLAnchorElement>(root, LINK_SELECTORS);
    links.forEach((a) => {
      if (a.querySelector(`.${FLAG_CLASS}`)) return;

      // 1) Prefer explicit asset via data attribute (Dynamic Media, with DAM fallback)
      const explicitAsset = a.getAttribute('data-flag-asset')?.trim();
      if (explicitAsset) {
        const assetCountry =
          getFileNameWithoutExtension(explicitAsset).toUpperCase() || (a.textContent ?? '').trim();
        const img = document.createElement('img');
        img.className = FLAG_CLASS;
        img.src = resolveFlagSrc(explicitAsset);
        img.alt = assetCountry;
        img.width = 20;
        img.height = 14;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.style.outline = '1px solid #DADAE1';
        img.style.outlineOffset = '1px';
        a.prepend(img);
        return;
      }

      // 2) Fallback to hreflang-derived flag (Dynamic Media, with DAM fallback)
      const { country } = parseHreflang(a.getAttribute('hreflang'));
      if (!country || country.length !== 2) return;

      const img = document.createElement('img');
      img.className = FLAG_CLASS;
      img.src = resolveFlagSrc(country);
      img.alt = country.toUpperCase();
      img.width = 20;
      img.height = 14;
      img.loading = 'lazy';
      img.decoding = 'async';
      a.prepend(img);
    });
  }

  function wireClickCookie(root: HTMLElement) {
    const currentCountry = getCurrentCountry();
    if (currentCountry !== 'ch') return;
    const links = qsa<HTMLAnchorElement>(root, LINK_SELECTORS);
    links.forEach((a) => {
      a.addEventListener('click', () => {
        const { country, language } = parseHreflang(a.getAttribute('hreflang'));
        if (country === 'ch' && language) setCookie(LANG_COOKIE, language);
      });
    });
  }

  function wireCoreClickCookie(root: HTMLElement) {
    const currentCountry = getCurrentCountry();
    if (currentCountry !== 'ch') return;
    const links = Array.from(
      root.querySelectorAll<HTMLAnchorElement>('.cmp-languagenavigation__item-link')
    );
    links.forEach((a) => {
      a.addEventListener('click', () => {
        const { country, language } = parseHreflang(a.getAttribute('hreflang'));
        if (country === 'ch' && language) setCookie(LANG_COOKIE, language);
      });
    });
  }

  function wireSearch(root: HTMLElement) {
    const search = findNearestSearchInput(root);
    const items = qsa<HTMLElement>(root, ITEM_SELECTORS);
    if (!search || !items.length) return;
    const applyFilter = () => {
      const term = (search.value ?? '').trim().toLowerCase();
      const searching = term.length > 0;
      items.forEach((li) => {
        if (searching && isSeparator(li)) {
          li.style.display = 'none';
          return;
        }
        const text = textOfItem(li);
        li.style.display = searching && !text.includes(term) ? 'none' : '';
      });
    };
    applyFilter();
    search.addEventListener('input', applyFilter);
    search.addEventListener('change', applyFilter);
  }

  function initInstance(root: HTMLElement) {
    if (initialized.has(root)) return;
    initialized.add(root);
    wireSearch(root);
    addFlags(root); // flags only for our component
    wireClickCookie(root);
  }

  function initCoreInstance(root: HTMLElement) {
    if (coreInitialized.has(root)) return;
    coreInitialized.add(root);
    // no flags for the core component
    wireCoreClickCookie(root);
  }

  function onDocumentReady() {
    hideBreakLinesFromScreenReaders(document);
    document.querySelectorAll<HTMLElement>(ROOT_SEL).forEach(initInstance);
    document.querySelectorAll<HTMLElement>(CORE_SEL).forEach(initCoreInstance);

    const skipTags = new Set(['SCRIPT', 'STYLE', 'LINK', 'META']);
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement) || skipTags.has(node.tagName)) return;
          hideBreakLinesFromScreenReaders(node);
          if (node.matches?.(ROOT_SEL)) initInstance(node);
          else node.querySelectorAll?.<HTMLElement>(ROOT_SEL).forEach(initInstance);
          if (node.matches?.(CORE_SEL)) initCoreInstance(node);
          else node.querySelectorAll?.<HTMLElement>(CORE_SEL).forEach(initCoreInstance);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState !== 'loading') onDocumentReady();
  else document.addEventListener('DOMContentLoaded', onDocumentReady);
}

languageNavigator();
